import React, { useMemo, useState, useEffect } from "react";

import ProjectCard from "../components/ProjectCard";
import ProjectFormModal from "../components/ProjectFormModal";

import { useProject } from "../context/ProjectContext";
import { useCompany } from "../context/CompanyContext";

import { Logos } from "../assets/logos";

/* ===================================================== */
/* PROJECTS PAGE                                         */
/* ===================================================== */

export default function ProjectsPage({ openCreate = false }) {
  const { projects, loading, selectProject, removeProject } = useProject();
  const { companyId } = useCompany();

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* ===================================================== */
  /* AUTO OPEN CREATE                                      */
  /* ===================================================== */

  useEffect(() => {
    if (openCreate) setModalOpen(true);
  }, [openCreate]);

  /* ===================================================== */
  /* DELETE PROJECT                                        */
  /* ===================================================== */

  const handleDelete = async (projectId) => {
    try {
      setDeletingId(projectId);
      await removeProject(projectId);
    } catch (err) {
      console.error("[Projects] Delete failed:", err);
      alert("Failed to delete project");
    } finally {
      setDeletingId(null);
    }
  };

  /* ===================================================== */
  /* FILTER                                                */
  /* ===================================================== */

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;

    return projects.filter((p) =>
      `${p.name} ${p.description || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [projects, search]);

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="mx-auto max-w-7xl p-6">

      {/* ===================================================== */}
      {/* HEADER                                                */}
      {/* ===================================================== */}

      <div className="flex items-center justify-between mb-6">

        <div className="flex items-center gap-3">
          <img
            src={Logos.black?.square || "/logo.svg"}
            alt="HeadwaySpot"
            className="h-8 w-8"
          />

          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Projects
            </h1>

            <p className="text-sm text-gray-500">
              {loading
                ? "Loading..."
                : `${projects.length} project${
                    projects.length !== 1 ? "s" : ""
                  }`}
            </p>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="flex items-center gap-3">

          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-60 rounded-lg border px-3 py-2 text-sm"
          />

          <button
            onClick={() => setModalOpen(true)}
            className="rounded-lg bg-black px-4 py-2 text-white text-sm hover:bg-gray-800"
          >
            + New Project
          </button>
        </div>
      </div>

      {/* ===================================================== */}
      {/* CONTENT                                               */}
      {/* ===================================================== */}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

        {/* LOADING */}
        {loading && (
          <div className="col-span-full text-center text-gray-500 py-12">
            Loading projects...
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && filteredProjects.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">

            <p className="text-lg font-medium mb-2">
              No projects yet
            </p>

            <p className="text-sm mb-4">
              Create your first project or invite a contractor
            </p>

            <button
              onClick={() => setModalOpen(true)}
              className="px-4 py-2 bg-black text-white rounded-lg"
            >
              + Create Project
            </button>
          </div>
        )}

        {/* PROJECT GRID */}
        {!loading &&
          filteredProjects.map((project) => {
            const isOwner =
              String(project.clientCompanyId) === String(companyId);

            const isContractor =
              String(project.contractorCompanyId) === String(companyId);

            const plan =
              project.projectPlan || project.plan || "free";

            return (
              <ProjectCard
                key={project._id}
                project={project}

                /* 🔥 CORE UX SIGNALS */
                isOwner={isOwner}
                isContractor={isContractor}
                plan={plan}

                /* NAVIGATION */
                onClick={() => selectProject(project._id)}

                /* ACTIONS */
               onEdit={() => {
                setEditingProject(project);
                setModalOpen(true);
              }}
                onDelete={() => handleDelete(project._id)}
                deleting={deletingId === project._id}
              />
            );
          })}
      </div>

      {/* ===================================================== */}
      {/* MODAL                                                 */}
      {/* ===================================================== */}

      <ProjectFormModal
        open={modalOpen}
        project={editingProject}
        onClose={() => {
          setModalOpen(false);
          setEditingProject(null);
        }}
        onSaved={() => {
          setModalOpen(false);
          setEditingProject(null);
        }}
      />
    </div>
  );
}